// React
import React, { useEffect, useState, useCallback } from 'react';

// Redux
import { connect } from 'react-redux';
import { withRoomContext } from '../../../RoomContext';
import * as roomActions from '../../../actions/roomActions';

// Prop types
import PropTypes from 'prop-types';

// Message
import { FormattedMessage } from 'react-intl';

// Mui styles
import { withStyles, MuiThemeProvider, createTheme } from '@material-ui/core/styles';

// Mui core
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';

// Firebase
import { firestore } from '../../../lib/firebase';

const styles = (theme) =>
	({
		root : {
			zIndex : 99,
			width  : '100%',
			height : '100%'
		},
		dialogPaper : {
			padding                        : '1% 1%',
			width                          : '20vw',
			height                         : '35vw',
			[theme.breakpoints.down('lg')] : {
				width : '25vw'
			},
			[theme.breakpoints.down('md')] : {
				width : '50vw'
			},
			[theme.breakpoints.down('sm')] : {
				width : '70vw'
			},
			[theme.breakpoints.down('xs')] : {
				width : '90vw'
			}
		},
		titleText : {
			color          : '#999898',
			fontSize       : '0.9rem',
			display        : 'flex',
			justifyContent : 'center'
		},
		contentText : {
			display        : 'flex',
			justifyContent : 'space-between',
			width          : '100%',
			color          : '#999898',
			borderBottom   : '1px solid #999898',
			marginBottom   : '2rem',
			paddingBottom  : '0.3rem',
			alignItems     : 'end'
		},
		contentTitle : {
			fontSize : '0.8rem',
			color    : '#999898'
		},
		contentArea : {
			whiteSpace : 'break-spaces'
		},
		contentInfo : {
			fontSize   : '1.2rem',
			lineHeight : '1'
		},
		input : {
			width        : '60%',
			padding      : '0.4rem 0.5rem',
			borderRadius : '0.3rem',
			color        : '#999898',
			textAlign    : 'left',
			fontSize     : '1rem',
			border       : '1px solid #999898',
			'&:focus'    : {
				outline : 'none'
			}
		},
		content : {
			padding : '0 1.5rem'
		},
		underlined : {
			margin    : '1.5rem 0',
			borderTop : '1px solid #999898'
		},
		commentTitle : {
			fontSize   : '0.8rem',
			color      : '#999898',
			fontWeight : 'bold'
		},
		textArea : {
			fontSize                    : '0.8rem',
			color                       : '#999898',
			width                       : '100%',
			'& $textAreaNotchedOutline' : {
				borderColor : '#999898'
			},
			'&:hover $textAreaNotchedOutline' : {
				borderColor : '#999898'
			},
			'&.Mui-focused $textAreaNotchedOutline' : {
				borderColor : '#999898',
				borderWidth : '1px'
			}
		},
		textAreaNotchedOutline : {
			borderColor : '#999898'
		},
		comment : {
			paddingTop : '2rem'
		},
		commentInfo : {
			display        : 'flex',
			justifyContent : 'space-between',
			alignItems     : 'center',
			paddingTop     : '1rem',
			paddingBottom  : '1rem'
		},
		addBtn : {
			width      : '25%',
			border     : '1px solid #e97f00',
			background : '#e97f00',
			color      : 'white',
			padding    : '0',
			fontSize   : '0.7rem',
			'&:hover'  : {
				background : '#e97f00',
				color      : 'white'
			}
		},
		cancelBtn : {
			width      : '25%',
			border     : '1px solid #fff',
			background : '#878686',
			color      : 'white',
			padding    : '0',
			fontSize   : '0.7rem',
			'&:hover'  : {
				background : '#878686',
				color      : 'white'
			}
		},
		scrollBar : {
			overflow               : 'auto',
			height                 : '100%',
			'&::-webkit-scrollbar' : {
				background   : '#ccc',
				width        : '7px',
				height       : '7px',
				borderRadius : '100px'
			},
			'&::-webkit-scrollbar-thumb' : {
				background   : '#888',
				borderRadius : '100px',
				'&:hover'    : {
					background : '#555'
				}
			}
		}
	});

const theme = createTheme({
	overrides : {
		MuiDialogTitle : {
			root : {
				backgroundColor : 'transparent',
				color           : '#999898',
				width           : '100%'
			}
		},
		MuiDialogContent : {
			root : {
				display       : 'flex',
				flexDirection : 'column',
				padding       : '0.5rem 0'
			}
		},
		MuiDialogActions : {
			root : {
				justifyContent : 'center'
			},
			spacing : {
				'& > :not(:first-child)' : {
					marginLeft : '1.5rem'
				}
			}
		},
		MuiPaper : {
			rounded : {
				borderRadius : '0.5rem'
			}
		}
	}
});

const ReciprocalContentDialog = ({
	reciprocalContentDialogOpen,
	setReciprocalContentDialogOpen,
	classes,
	memo,
	setMemo
}) =>
{
	// Type step
	const STEP_COMMENT = {
		NEW  : 'new',
		ADD  : 'add',
		SAVE : 'save'
	};
	const [ changeStep, setChangeStep ] = useState(STEP_COMMENT.NEW);

	const [ reciprocalData, setReciprocalData ] = useState({});
	// Reciprocal person
	const [ messageName, setCommentName ] = useState('');
	// Reciprocal content
	const [ message, setComment ] = useState('');

	const getDateString = (timestamp) =>
	{
		const date = timestamp ? new Date(timestamp) : new Date();
		const month = (`0${date.getMonth() + 1}`).slice(-2);
		const day = (`0${date.getDate()}`).slice(-2);
		const hour = (`0${date.getHours()}`).slice(-2);
		const min = (`0${date.getMinutes()}`).slice(-2);

		return `${date.getFullYear()}/${month}/${day} ${hour}:${min}`;
	};

	useEffect(() =>
	{
		if (memo)
		{
			setReciprocalData({
				id        : memo.id,
				title     : memo.title,
				name      : memo.user,
				sms       : memo.sms,
				timestamp : memo.timestamp,
				message   : memo.message,
				comment   : memo.comment
			});
		}
	}, [ memo ]);

	const handleChangeName = (e) =>
	{
		setCommentName(e.target.value);
	};

	const handleChangeText = (e) =>
	{
		setComment(e.target.value);
	};

	const handleClosePopup = useCallback(() =>
	{
		setReciprocalContentDialogOpen(false);
		setMemo(null);
	}, [ setReciprocalContentDialogOpen, setMemo ]);

	const handleNewComment = () =>
	{
		setChangeStep(STEP_COMMENT.ADD);
	};

	const handleBackPopup = () =>
	{
		const { comment } = reciprocalData;

		comment.pop();
		setChangeStep(STEP_COMMENT.ADD);
	};

	const handleAddComment = () =>
	{
		if (messageName === '' || message === '')
		{
			return;
		}

		const { comment, title, sms, name } = reciprocalData;
		const timestamp = Date.now();

		comment.push({
			user      : messageName,
			timestamp : timestamp,
			name      : name,
			sms       : sms,
			message   : message,
			title     : title
		});

		setReciprocalData({
			...reciprocalData,
			timestamp : timestamp,
			comment   : [ ...comment ]
		});

		setChangeStep(STEP_COMMENT.SAVE);
	};

	const handleSaveComment = useCallback(() =>
	{
		firestore.collection('memos').doc(memo.id)
			.update({
				timestamp : memo.timestamp,
				comment   : memo.comment
			});

		handleClosePopup();
	}, [ memo, handleClosePopup ]);

	return (
		<MuiThemeProvider theme={theme}>
			<Dialog
				className={classes.root}
				open={reciprocalContentDialogOpen}
				onClose={handleClosePopup}
				classes={{
					paper : classes.dialogPaper
				}}
			>
				<DialogTitle id='form-dialog-title' className={classes.titleText}>
					<Typography className={classes.titleText}>
						対応内容
					</Typography>
				</DialogTitle>
				<DialogContent>
					<Box className={classes.content}>
						<Box className={classes.contentText}>
							<Typography className={classes.contentTitle}>対応者</Typography>
							<input
								defaultValue={reciprocalData.name}
								className={classes.input}
								type='text'
							/>
						</Box>
					</Box>
					<Box className={classes.scrollBar}>
						<Box className={classes.content}>
							<Box className={classes.contentText}>
								<Typography className={classes.contentTitle}>日付</Typography>
								<Typography className={classes.contentInfo}>
									{getDateString(reciprocalData.date)}
								</Typography>
							</Box>
							<Typography className={`${classes.contentTitle} ${classes.contentArea}`}>
								{reciprocalData.message}
							</Typography>
							{reciprocalData.comment &&
								reciprocalData.comment.map((item, index) => (
									<Box key={index}>
										<Box className={classes.underlined} />
										<Typography className={classes.commentTitle}>
											{`[${getDateString(item.date)}] ${item.user}`}
										</Typography>
										<Typography className={`${classes.contentTitle} ${classes.contentArea}`}>
											{item.message}
										</Typography>
									</Box>
								))
							}
							{changeStep === STEP_COMMENT.ADD &&
								<Box className={classes.comment}>
									<TextField
										classes={{
											root : classes.textArea
										}}
										InputProps={{
											classes : {
												root           : classes.textArea,
												notchedOutline : classes.textAreaNotchedOutline
											}
										}}
										multiline
										minRows={5}
										variant='outlined'
										value={message || ''}
										onChange={handleChangeText}
									/>
									<Box className={classes.commentInfo}>
										<Typography className={classes.contentTitle}>追記署名</Typography>
										<input
											className={classes.input}
											type='text'
											value={messageName || ''}
											onChange={handleChangeName}
										/>
									</Box>
								</Box>
							}
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					{changeStep !== STEP_COMMENT.SAVE &&
						<Button
							className={classes.cancelBtn}
							onClick={handleClosePopup}
						>
							<FormattedMessage id='label.close' defaultMessage='Close'/>
						</Button>
					}
					{changeStep === STEP_COMMENT.SAVE &&
						<Button
							className={classes.cancelBtn}
							onClick={handleBackPopup}
						>
							<FormattedMessage id='label.back' defaultMessage='Back'/>
						</Button>
					}
					{changeStep === STEP_COMMENT.NEW &&
						<Button
							className={classes.addBtn}
							onClick={handleNewComment}
						>
							<FormattedMessage id='label.add' defaultMessage='Add'/>
						</Button>
					}
					{changeStep === STEP_COMMENT.ADD &&
						<Button
							className={classes.addBtn}
							onClick={handleAddComment}
						>
							<FormattedMessage id='label.next' defaultMessage='Next'/>
						</Button>
					}
					{changeStep === STEP_COMMENT.SAVE &&
						<Button
							className={classes.addBtn}
							onClick={handleSaveComment}
						>
							<FormattedMessage id='label.save' defaultMessage='Save'/>
						</Button>
					}
				</DialogActions>
			</Dialog>
		</MuiThemeProvider>
	);
};

ReciprocalContentDialog.propTypes =
{
	reciprocalContentDialogOpen    : PropTypes.bool.isRequired,
	setReciprocalContentDialogOpen : PropTypes.func.isRequired,
	classes                        : PropTypes.object.isRequired,
	memo                           : PropTypes.object.isRequired,
	setMemo                        : PropTypes.func.isRequired
};

const makeMapStateToProps = () =>
{
	const mapStateToProps = (state) =>
	{
		return {
			reciprocalContentDialogOpen : state.room.reciprocalContentDialogOpen,
			memo                        : state.room.memo
		};
	};

	return mapStateToProps;
};

const mapDispatchToProps = (dispatch) =>
{
	return {
		setReciprocalContentDialogOpen : (reciprocalContentDialogOpen) =>
		{
			dispatch(roomActions.setReciprocalContentDialogOpen(reciprocalContentDialogOpen));
		},
		setMemo : (memo) =>
		{
			dispatch(roomActions.setMemo(memo));
		}
	};
};

export default withRoomContext(connect(
	makeMapStateToProps,
	mapDispatchToProps,
	null,
	{
		areStatesEqual : (next, prev) =>
		{
			return (
				prev.room.reciprocalContentDialogOpen === next.room.reciprocalContentDialogOpen &&
				prev.room.memo === next.room.memo
			);
		}
	}
)(withStyles(styles)(ReciprocalContentDialog)));